import { CSSProperties, Fragment } from 'react';
import Colors from 'modules/Colors';
import { usePalette } from 'react-palette';
import { clsx, makeKey, styx } from 'modules/Utils';
import ColorPickerInput from './ColorPickerInput';

interface ImagePaletteProps {
    className: string;
    style?: CSSProperties;
    styleColorPaletteContainer?: CSSProperties;
    imageUrl: string;
    selectedColor: string;
    showBlackAndWhite?: boolean;
    showBlackAndWhiteOnly?: boolean;
    selectColor: (color: string) => void;
    onImageLoad?: () => void;
    autoSelectFirstColor?: boolean;
    autoSelectSecondColor?: boolean;
    isInputColorPicker?: boolean;
    size?: number;
}

/**
 * Generates an array of default colors based on Symphony's purple theme
 * @param count - Number of colors to generate in the gradient
 * @returns Array of hex color codes
 */
const generateDefaultColors = (count: number = 6): string[] => {
    // Ensure we always include #8800ff in the results
    const purpleHex = '#8800ff';

    const startHue = 271;  // Purple hue (#8800ff)
    const startSaturation = 85;
    const startLightness = 25;  // Darker

    const endSaturation = 75;
    const endLightness = 80;  // Lighter

    let colors = Array.from({ length: count - 1 }, (_, i) => {
        // Calculate the percentage of progression through the gradient
        const progress = i / (count - 2);

        // Interpolate between start and end values
        const saturation = startSaturation + (endSaturation - startSaturation) * progress;
        const lightness = startLightness + (endLightness - startLightness) * progress;

        return `hsl(${startHue}, ${saturation}%, ${lightness}%)`;
    }).map(hslColor => {
        // Convert HSL to hex (keeping existing conversion logic)
        const div = document.createElement('div');
        div.style.color = hslColor;
        document.body.appendChild(div);
        const rgbColor = window.getComputedStyle(div).color;
        document.body.removeChild(div);

        const [r, g, b] = rgbColor.match(/\d+/g)!.map(Number);
        return '#' + [r, g, b].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        }).join('');
    });

    // Insert #8800ff at the middle position to ensure it's always included
    const middleIndex = Math.floor(colors.length / 2);
    colors.splice(middleIndex, 0, purpleHex);

    return colors;
};

const ImagePalette = ({
    className,
    style,
    styleColorPaletteContainer,
    imageUrl,
    selectedColor,
    showBlackAndWhite,
    showBlackAndWhiteOnly = false,
    selectColor,
    isInputColorPicker = false,
    size = 46,
}: ImagePaletteProps) => {
    const { data } = usePalette(imageUrl);

    const renderValues: string[] = []
    const uniqueValuesData = new Set(Object.values(data));
    uniqueValuesData.forEach((color) => { if (color) renderValues.push(color) });

    const defaultColors = generateDefaultColors();

    // Use default colors if no colors were extracted from the image
    const colorsToRender = renderValues.length > 0 ? renderValues : defaultColors;

    const renderBox = (color: string) => {
        let colorBoxContainerStyle = sty.colorBoxContainer;
        if (selectedColor === color) {
            colorBoxContainerStyle = styx(
                sty.colorBoxContainer,
                sty.colorBoxSelected,
            )
        }

        return (
            <div className="selectedColorBox" style={colorBoxContainerStyle}>
                <div
                    className="colorBox"
                    style={styx(
                        sty.colorBox,
                        {
                            backgroundColor: color,
                            border: color === "#FFFFFF" ? "1px solid #adb7bc" : "none",
                            width: size,
                            height: size,
                        }
                    )}
                    onClick={() => selectColor(color)}
                />
            </div>
        )
    }

    // NOTE: render()
    return (
        <div
            className={clsx('imagePaletteComponent', className)}
            style={styx(sty.imagePaletteComponent, style)}
        >
            {isInputColorPicker &&
                <ColorPickerInput
                    className="imagePaletteColorPickerInput"
                    selectedColor={selectedColor}
                    selectColor={selectColor}
                />
            }
            <div
                className="colorPaletteContainer"
                style={styx(
                    {
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center'
                    },
                    styleColorPaletteContainer
                )}
            >
                {!showBlackAndWhiteOnly && colorsToRender.map((color, index) =>
                    <Fragment key={makeKey(color, index)}>
                        {renderBox(color)}
                    </Fragment>
                )}
                {showBlackAndWhite &&
                    <>
                        {renderBox("#FFFFFF")}
                        {renderBox("#000000")}
                    </>
                }
            </div>
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    imagePaletteComponent: {
        display: "flex",
        gap: 8,
        alignItems: "center",
    },
    colorBoxContainer: {
        boxSizing: "border-box",
        borderRadius: 12,
        padding: 4,
    },
    colorBoxSelected: {
        border: `2px solid ${Colors.purple}`,
    },
    colorBox: {
        fontSize: 12,
        color: "white",
        borderRadius: 7,
        cursor: "pointer",
    },
}

export default ImagePalette;
